import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { ObserveProperty } from "@calaosoft/osapp-common/observable/decorators/observe-property.decorator";
import { ObservableProperty } from "@calaosoft/osapp-common/observable/models/observable-property";
import { secure } from "@calaosoft/osapp-common/rxjs/operators/secure";
import { IonicSafeString } from "@ionic/core";
import { take, tap } from "rxjs";
import { IUiResponse } from "../../../../model/uiMessage/IUiResponse";
import { DestroyableComponentBase } from "../../../utils/components/destroyable-component-base";
import { IToast } from "../../models/itoast";
import { IToastButton } from "../../models/itoast-button";
import { IToastResponse } from "../../models/itoast-response";

@Component({
	selector: "calao-toast",
	templateUrl: "./toast.component.html",
	styleUrls: ["./toast.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class ToastComponent extends DestroyableComponentBase implements IToast, OnInit {
	//#region FIELDS

	/** Événement levé lors de la suppression du toast. */
	@Output("onDismiss") private readonly moDismissEvent = new EventEmitter<string>();
	/** Événement levé lors d'un click sur un bouton d'un toast. */
	@Output("onResponse") private readonly moResponseEvent = new EventEmitter<IToastResponse<any>>();

	private mnDefaultFadeDuration: number = 200; // Millisecondes

	//#endregion FIELDS

	//#region PROPERTIES

	/** Identifiant du toast.
	 * @implements
	 */
	@Input() public id?: string;

	/** Titre optionnel du toast affiché en gras.
	 * @implements
	 */
	@Input() public title?: string;
	@ObserveProperty<ToastComponent>({ sourcePropertyKey: "title" })
	public readonly obsTitle = new ObservableProperty<string>();

	/** Description optionnelle affichée sous le titre.
	 * @implements
	 */
	@Input() public description?: string | IonicSafeString;
	@ObserveProperty<ToastComponent>({ sourcePropertyKey: "description" })
	public readonly obsDescription = new ObservableProperty<string>();

	/** Nombre de millisecondes avant disparition du toast.
	 * @implements
	 */
	@Input() public duration?: number;
	@ObserveProperty<ToastComponent>({ sourcePropertyKey: "duration" })
	public readonly obsDuration = new ObservableProperty<number>();

	/** Tableau des boutons à afficher dans le toast.
	 * @implements
	 */
	@Input() public buttons?: IToastButton[];
	@ObserveProperty<ToastComponent>({ sourcePropertyKey: "buttons" })
	public readonly obsButtons = new ObservableProperty<IToastButton[]>();

	/** `true` si l'utilisateur peut dismiss le toast avec la croix ou le swipe, sinon `false`. */
	@Input() public canDismiss?: boolean;
	@ObserveProperty<ToastComponent>({ sourcePropertyKey: "canDismiss" })
	public readonly obsCanDismiss = new ObservableProperty<boolean>();

	/** Nombre de milliseconde avant que l'utilisateur puisse dismiss le toast. */
	@Input() public canDismissAfter?: number;
	@ObserveProperty<ToastComponent>({ sourcePropertyKey: "canDismissAfter" })
	public readonly obsCanDismissAfter = new ObservableProperty<number>();

	/** `true` si la croix pour fermer le toast doit être affichée, sinon `false`. */
	@Input() public showDismissButton?: boolean;
	@ObserveProperty<ToastComponent>({ sourcePropertyKey: "showDismissButton" })
	public readonly obsShowDismissButton = new ObservableProperty<boolean>();

	/** Active le fondu à la création. */
	public readonly obsFadeIn = new ObservableProperty<boolean>(true);
	/** Active le fondu à la suppression. */
	public readonly obsFadeOut = new ObservableProperty<boolean>(false);

	//#endregion PROPERTIES

	//#region METHODS

	constructor() {
		super();
	}

	public ngOnInit(): void {
		this.obsDuration.value$
			.pipe(
				take(1),
				tap((pnDuration?: number) => {
					if (pnDuration) {
						const lnTotaDuration: number = pnDuration + (this.obsCanDismissAfter.value ?? 0);
						if (lnTotaDuration > this.mnDefaultFadeDuration)
							setTimeout(() => (this.obsFadeOut.value = true), lnTotaDuration - this.mnDefaultFadeDuration);

						setTimeout(() => {
							this.moDismissEvent.emit(this.id);
						}, lnTotaDuration);
					}
				}),
				secure(this)
			)
			.subscribe();

		setTimeout(() => (this.obsFadeIn.value = false), this.mnDefaultFadeDuration);

		if (this.obsCanDismissAfter.value)
			setTimeout(() => (this.obsCanDismiss.value = true), this.obsCanDismissAfter.value);
		else this.obsCanDismiss.value = true;
	}

	public onCloseClicked(): void {
		this.fadeOutAndDismissToast();
	}

	public onSwipe(): void {
		if (this.obsCanDismiss.value) this.fadeOutAndDismissToast();
	}

	public onButtonClicked(psButtonId?: string): void {
		const loButton: IToastButton | undefined = this.buttons?.find(
			(poButton: IToastButton) => poButton.id === psButtonId
		);
		const loResponse = loButton?.handler ? loButton.handler() : undefined;
		if (this.id && psButtonId) {
			if (loResponse instanceof Promise) {
				loResponse.then((poResponse: any) => {
					if (this.id) this.emitResponse(this.id, (loResponse as IUiResponse<any>).response ?? poResponse);
				});
			} else this.emitResponse(this.id, (loResponse as IUiResponse<any>).response ?? loResponse);
		}
		this.fadeOutAndDismissToast();
	}

	private emitResponse(psToastId: string, poResponse: any) {
		this.moResponseEvent.emit({ toastId: psToastId, response: poResponse });
	}

	private fadeOutAndDismissToast(): void {
		this.obsFadeOut.value = true;
		setTimeout(() => this.moDismissEvent.emit(this.id), this.mnDefaultFadeDuration);
	}

	//#endregion METHODS
}
